import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, MarkerF, CircleF, useLoadScript, Autocomplete } from '@react-google-maps/api';
import { Box, Button, CircularProgress, InputAdornment, Slider, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LOADER } from '../config/googleMaps';
import { MAX_RADIUS_METERS, metersFromSlider, parsePresenceRadius, radiusErrorMessage, sliderPosition } from '../utils/presenceRadius';

const defaultCenter = { lat: -23.55052, lng: -46.633308 }; // São Paulo

// Tema dark customizado para o Google Maps
export const darkMapStyles = [
  {
    elementType: 'geometry',
    stylers: [{ color: '#0B1C3E' }],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#0B1C3E' }],
  },
  {
    elementType: 'labels.text.fill',
    stylers: [{ color: '#F5F7FA' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#0a1929' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#4a90a4' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#1a2332' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#2a3441' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9ca5b4' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#2a3441' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#3a4451' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#b3bccb' }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#1a2332' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9ca5b4' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#0f1e2e' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6b9c78' }],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#1a2332' }],
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [{ color: '#2a3441' }],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#2a3441' }],
  },
  {
    featureType: 'administrative',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9ca5b4' }],
  },
];

const MapRadiusPicker = ({
  value = {},
  onChange,
  mapHeight = 320,
}) => {
  const apiKey = GOOGLE_MAPS_API_KEY;
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const frameHeight = isMd ? 360 : isSm ? 320 : 260;
  
  const { isLoaded, loadError } = useLoadScript(GOOGLE_MAPS_LOADER);

  const [autocomplete, setAutocomplete] = useState(null);
  const [addressNotice, setAddressNotice] = useState('');
  const [radiusDraft, setRadiusDraft] = useState(() => {
    const source = value.radius_m ?? value.radius;
    return source == null || source === '' ? '' : String(source);
  });
  const [radiusError, setRadiusError] = useState('');
  const valueRef = useRef(value);
  const pointRef = useRef({
    latitude: value.latitude,
    longitude: value.longitude,
    endereco: value.endereco,
  });
  const geocodeSeq = useRef(0);
  const geocoderRef = useRef(null);
  const externalRadius = useRef(value.radius_m ?? value.radius ?? null);
  valueRef.current = value;
  pointRef.current = {
    latitude: value.latitude,
    longitude: value.longitude,
    endereco: value.endereco,
  };

  const hasCoordinates = value.latitude != null && value.latitude !== ''
    && value.longitude != null && value.longitude !== ''
    && !Number.isNaN(Number(value.latitude))
    && !Number.isNaN(Number(value.longitude));

  const center = useMemo(() => {
    if (hasCoordinates) {
      return {
        lat: Number(value.latitude),
        lng: Number(value.longitude),
      };
    }
    return defaultCenter;
  }, [hasCoordinates, value.latitude, value.longitude]);

  const radiusSource = value.radius_m ?? value.radius;
  const hasRadius = radiusSource != null && radiusSource !== '' && !Number.isNaN(Number(radiusSource));
  const radius = hasRadius ? Number(radiusSource) : null;

  // Criar ícone customizado do marker
  const customMarkerIcon = useMemo(() => {
    if (!isLoaded || typeof window === 'undefined' || !window.google?.maps) {
      return undefined;
    }
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" fill="#2FC5A9" stroke="#0B1C3E" stroke-width="2"/>
          <circle cx="12" cy="12" r="4" fill="#0B1C3E"/>
        </svg>
      `),
      scaledSize: new window.google.maps.Size(24, 24),
      anchor: new window.google.maps.Point(12, 12),
    };
  }, [isLoaded]);

  const updateValue = useCallback(
    (partial) => {
      const current = valueRef.current || {};
      const currentRadius = current.radius_m ?? current.radius ?? null;
      onChange?.({
        latitude: Object.prototype.hasOwnProperty.call(partial, 'latitude') ? partial.latitude : (current.latitude ?? null),
        longitude: Object.prototype.hasOwnProperty.call(partial, 'longitude') ? partial.longitude : (current.longitude ?? null),
        radius_m: Object.prototype.hasOwnProperty.call(partial, 'radius_m') || Object.prototype.hasOwnProperty.call(partial, 'radius')
          ? (partial.radius_m ?? partial.radius)
          : currentRadius,
        endereco: Object.prototype.hasOwnProperty.call(partial, 'endereco') ? partial.endereco : current.endereco,
      });
    },
    [onChange]
  );

  useEffect(() => {
    if (externalRadius.current === radius) return;
    externalRadius.current = radius;
    setRadiusDraft(radius == null ? '' : String(radius));
    setRadiusError('');
  }, [radius]);

  const reverseGeocode = useCallback((latitude, longitude) => {
    const seq = geocodeSeq.current + 1;
    geocodeSeq.current = seq;
    const enderecoAtStart = pointRef.current.endereco;
    if (!window.google?.maps?.Geocoder) {
      setAddressNotice('Não foi possível obter o endereço. Informe a referência manualmente. As coordenadas permanecem válidas.');
      return;
    }
    if (!geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
    geocoderRef.current.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
      if (seq !== geocodeSeq.current) return;
      const latest = pointRef.current;
      const samePoint = Number(latest.latitude) === Number(latitude) && Number(latest.longitude) === Number(longitude);
      if (!samePoint || latest.endereco !== enderecoAtStart) return;
      if (status === 'OK' && results?.[0]?.formatted_address) {
        setAddressNotice('');
        updateValue({ latitude, longitude, endereco: results[0].formatted_address });
        return;
      }
      setAddressNotice('Não foi possível obter o endereço. Informe a referência manualmente. As coordenadas permanecem válidas.');
    });
  }, [updateValue]);

  const selectPoint = useCallback((latitude, longitude) => {
    pointRef.current = {
      latitude,
      longitude,
      endereco: valueRef.current?.endereco,
    };
    updateValue({ latitude, longitude });
    reverseGeocode(latitude, longitude);
  }, [reverseGeocode, updateValue]);

  const handlePlaceChanged = useCallback(() => {
    if (!autocomplete) return;
    const place = autocomplete.getPlace();
    if (!place.geometry) return;
    const newCenter = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    geocodeSeq.current += 1;
    setAddressNotice('');
    pointRef.current = {
      latitude: newCenter.lat,
      longitude: newCenter.lng,
      endereco: place.formatted_address,
    };
    updateValue({ latitude: newCenter.lat, longitude: newCenter.lng, endereco: place.formatted_address });
  }, [autocomplete, updateValue]);

  const handleMapClick = useCallback(
    (event) => {
      const lat = event.latLng?.lat();
      const lng = event.latLng?.lng();
      if (lat == null || lng == null) return;
      selectPoint(lat, lng);
    },
    [selectPoint]
  );

  const handleMarkerDragEnd = useCallback(
    (event) => {
      const lat = event.latLng?.lat();
      const lng = event.latLng?.lng();
      if (lat == null || lng == null) return;
      selectPoint(lat, lng);
    },
    [selectPoint]
  );

  const handleRadiusInput = (event) => {
    const text = event.target.value;
    setRadiusDraft(text);
    const parsed = parsePresenceRadius(text);
    if (!parsed.ok) {
      setRadiusError(radiusErrorMessage);
      return;
    }
    setRadiusError('');
    externalRadius.current = parsed.value;
    updateValue({ radius_m: parsed.value });
  };

  const handleRadiusSlider = (_event, position) => {
    const next = metersFromSlider(Array.isArray(position) ? position[0] : position);
    externalRadius.current = next;
    setRadiusDraft(String(next));
    setRadiusError('');
    updateValue({ radius_m: next });
  };

  const mapRef = useRef(null);

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
    map.setCenter(center);
    map.setZoom(hasCoordinates ? 16 : 12);
    window.google?.maps?.event?.trigger(map, 'resize');
  }, [center, hasCoordinates]);

  const frameArea = () => {
    const map = mapRef.current;
    if (!map || !hasCoordinates || !window.google?.maps) return;
    const meters = radius > 0 ? radius : 100;
    const circle = new window.google.maps.Circle({
      center,
      radius: meters,
    });
    const bounds = circle.getBounds();
    if (bounds) map.fitBounds(bounds);
  };

  if (!apiKey) {
    return (
      <Box sx={{ p: 3, borderRadius: 2, border: '1px solid rgba(68,224,198,0.12)', background: 'rgba(11,28,62,0.4)' }}>
        <Typography color="error">
          Google Maps API Key não configurada. Defina REACT_APP_GOOGLE_MAPS_API_KEY para usar o mapa.
        </Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Erro ao carregar o Google Maps. Verifique a chave informada.</Typography>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: mapHeight }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <Autocomplete onLoad={setAutocomplete} onPlaceChanged={handlePlaceChanged}>
        <TextField
          fullWidth
          label="Buscar endereço"
          variant="outlined"
          defaultValue={value.endereco || ''}
          placeholder="Digite o endereço ou nome do local"
        />
      </Autocomplete>
      <Box
        style={{ width: '100%', height: frameHeight, minHeight: 260, position: 'relative', overflow: 'hidden' }}
        sx={{
          width: '100%',
          maxWidth: '100%',
          height: { xs: 260, sm: 320, md: 360 },
          minHeight: 260,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          border: '1px solid rgba(68,224,198,0.2)',
          boxShadow: '0 4px 20px rgba(47, 197, 169, 0.1)',
        }}
      >
        <GoogleMap
          onLoad={handleMapLoad}
          mapContainerStyle={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          center={center}
          onClick={handleMapClick}
          options={{
            disableDefaultUI: false,
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: true,
            styles: darkMapStyles,
            backgroundColor: '#0B1C3E',
          }}
        >
          {hasCoordinates ? (
            <MarkerF
              position={center}
              draggable
              onDragEnd={handleMarkerDragEnd}
              icon={customMarkerIcon}
            />
          ) : null}
          {hasCoordinates && radius > 0 ? (
            <CircleF
              center={center}
              radius={radius}
              options={{
                strokeColor: '#14B8A6',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: '#14B8A6',
                fillOpacity: 0.18,
                clickable: false,
                editable: false,
                zIndex: 1,
              }}
            />
          ) : null}
        </GoogleMap>
      </Box>
      <Button variant="outlined" onClick={frameArea} disabled={!hasCoordinates} sx={{ alignSelf: 'flex-start' }}>
        Enquadrar área
      </Button>
      {addressNotice ? (
        <Typography variant="body2" color="warning.main">{addressNotice}</Typography>
      ) : null}
      <Box sx={{ background: 'rgba(11,28,62,0.45)', borderRadius: 2, p: 2, border: '1px solid rgba(68,224,198,0.12)', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', overflow: 'visible' }}>
        <TextField
          label="Raio de presença"
          type="number"
          fullWidth
          value={radiusDraft}
          onChange={handleRadiusInput}
          error={Boolean(radiusError)}
          helperText={radiusError || `Maior que 0 e até ${MAX_RADIUS_METERS} metros.`}
          inputProps={{
            min: 0,
            max: MAX_RADIUS_METERS,
            step: 1,
            inputMode: 'decimal',
            'aria-label': 'Raio de presença em metros',
          }}
          InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }}
          sx={{ maxWidth: '100%', minWidth: 0 }}
        />
        <Slider
          value={sliderPosition(radius)}
          min={0}
          max={1000}
          step={1}
          aria-label="Ajuste do raio de presença"
          valueLabelDisplay="auto"
          valueLabelFormat={() => (radius == null ? '—' : `${radius} m`)}
          onChange={handleRadiusSlider}
          slotProps={{
            root: {
              style: {
                display: 'inline-block',
                width: '100%',
                height: 4,
                boxSizing: 'content-box',
                padding: '13px 0',
                position: 'relative',
                cursor: 'pointer',
                touchAction: 'none',
                marginTop: 16,
              },
            },
            rail: {
              style: {
                display: 'block',
                position: 'absolute',
                width: '100%',
                height: 4,
                borderRadius: 2,
                backgroundColor: 'rgba(47, 197, 169, 0.35)',
                top: '50%',
                transform: 'translateY(-50%)',
              },
            },
            track: {
              style: {
                display: 'block',
                position: 'absolute',
                height: 4,
                borderRadius: 2,
                backgroundColor: '#2FC5A9',
                top: '50%',
                transform: 'translateY(-50%)',
              },
            },
            thumb: {
              style: {
                display: 'flex',
                position: 'absolute',
                boxSizing: 'border-box',
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: '#2FC5A9',
                border: '2px solid #F5F7FA',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                cursor: 'grab',
              },
            },
          }}
        />
      </Box>
    </Stack>
  );
};

export default MapRadiusPicker;
